/**
 * Belegliste der Startseite — seitenweise nachladen, Suche über ALLE Belege.
 *
 * Bis 19.10.2026 lud die App nur die neuesten 60 von über 550 Belegen und
 * suchte darin. Ein älterer Beleg (Stauden-Ihm, 799,57 €, Platz 92) war damit
 * weder zu sehen noch zu finden. Jetzt:
 *  - die Liste lädt in Seiten zu 60 nach (`mehrLaden`, am Listenende automatisch),
 *  - ab zwei Zeichen fragt die Suche den Server (`?q=`), der alle Belege durchsucht.
 *
 * Beide Abfragen liegen unter dem Schlüssel „belege-recent", damit die
 * vorhandenen `invalidateQueries(["belege-recent"])` sie weiter auffrischen.
 */
import { useEffect, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api, type Beleg } from "./api";

const SEITE = 60;
const SUCH_LIMIT = 200;
/** Ab so vielen Zeichen sucht der Server; darunter filtert die App die geladenen. */
export const SERVER_SUCHE_AB = 2;

export function useBelegListe(suchbegriff: string) {
  const term = suchbegriff.trim();
  const [verzoegert, setVerzoegert] = useState(term);
  useEffect(() => {
    const t = setTimeout(() => setVerzoegert(term), 300);
    return () => clearTimeout(t);
  }, [term]);

  const liesGerade = (items: Beleg[]) => items.some((b) => b.ki_laeuft);

  const liste = useInfiniteQuery({
    queryKey: ["belege-recent", "liste"],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.listBelege({ limit: SEITE, offset: pageParam }),
    getNextPageParam: (letzte, seiten) => {
      const geladen = seiten.reduce((n, s) => n + s.items.length, 0);
      return letzte.items.length > 0 && geladen < letzte.total ? geladen : undefined;
    },
    // Solange Clara noch einen Beleg liest, alle 3 s nachsehen — danach Ruhe.
    refetchInterval: (q) =>
      liesGerade((q.state.data?.pages ?? []).flatMap((s) => s.items)) ? 3000 : false,
  });

  const serverSuche = verzoegert.length >= SERVER_SUCHE_AB;
  const suche = useQuery({
    queryKey: ["belege-recent", "suche", verzoegert],
    queryFn: () => api.listBelege({ q: verzoegert, limit: SUCH_LIMIT }),
    enabled: serverSuche,
    placeholderData: keepPreviousData,
  });

  // Doppelte ausschließen: verschiebt sich die Liste zwischen zwei Seiten
  // (neuer Scan), käme ein Beleg sonst zweimal.
  const gesehen = new Set<string>();
  const geladen = (liste.data?.pages ?? []).flatMap((s) => s.items).filter((b) => {
    if (gesehen.has(b.id)) return false;
    gesehen.add(b.id);
    return true;
  });
  const gesamt = liste.data?.pages[0]?.total ?? 0;

  return {
    /** Was die Seite anzeigen und filtern soll. */
    belege: serverSuche ? suche.data?.items ?? [] : geladen,
    /** true = `belege` ist schon das Suchergebnis des Servers, nicht mehr in der App filtern. */
    serverSuche,
    sucheLaeuft: serverSuche && (suche.isFetching || verzoegert !== term),
    suchTreffer: suche.data?.total ?? 0,
    suchGekappt: serverSuche && (suche.data?.total ?? 0) > (suche.data?.items.length ?? 0),
    geladen: geladen.length,
    gesamt,
    hatMehr: !serverSuche && !!liste.hasNextPage,
    mehrLaden: () => { if (liste.hasNextPage && !liste.isFetchingNextPage) void liste.fetchNextPage(); },
    laedtMehr: liste.isFetchingNextPage,
    isLoading: liste.isLoading || (serverSuche && suche.isLoading),
    isError: liste.isError || suche.isError,
    error: (liste.error || suche.error) as Error | null,
  };
}
